/**
 * Resource Allocation Report — full page, booking-driven.
 * Access Evo Design System compliant.
 *
 * Primary personas:
 *   Resource Manager — daily working view of who is booked to what,
 *                       at what allocation, and where conflicts / gaps exist.
 *   PMO             — weekly governance across the project portfolio,
 *                       staffing status per project.
 *
 * Two view modes:
 *   Resource view — one group header per resource, one booking per table row.
 *   Project view  — one group header per project, one resource per table row.
 *
 */
import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfQuarter,
  endOfQuarter,
  addDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  addQuarters,
  subQuarters,
  parseISO,
  startOfDay,
  max as dateMax,
  min as dateMin,
  isWithinInterval,
  isBefore,
  isAfter,
} from 'date-fns';
import {
  AlertTriangle,
  BarChart2,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Download,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  ExternalLink,
  Loader2,
  PlusCircle,
  Users,
  Filter,
  X,
  SlidersHorizontal,
  Settings2,
  RotateCcw,
  GripVertical,
} from 'lucide-react';
import { WhatIfConflictResolutionOverlay } from '@/components/calendar/WhatIfConflictResolutionOverlay';
import type { ConflictItem } from '@/components/calendar/WhatIfConflictResolutionOverlay';
import { partitionConflictItems } from '@/utils/resolve-conflict-selection';
import type { RebalancingSuggestion } from '@/lib/utils/burnout-risk';
import { buildReassignmentMutations, buildReductionMutations, buildDelayMutations } from '@/utils/resolve-conflict-distribution';


import { FeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { HelpButton } from '@/components/help/HelpButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetHeader, SheetFooter } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Check } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useScreenData } from '@/hooks/useScreenData';
import { useReportScope } from '@/hooks/useReportScope';
import { useScrollbarGutter } from '@/hooks/useScrollbarGutter';
import { useCompany } from '@/contexts/CompanyContext';
import { absencesApi, bookingsApi } from '@/services/api';
import { mapApiBookingToBooking } from '@/hooks/use-date-range-bookings';
import { mockAbsences, mockBankHolidays } from '@/lib/mock-data';
import type { Absence, BankHoliday, Booking, Resource, WorkingHoursProfile, AppSettings, DurationType, HalfDayPeriod, PlannerNavContext } from '@/types';
import { bookingSegmentsOverlap } from '@/utils/booking-conflict';
import { isWorkingHoursProfileConfigured } from '@/utils/heatmap-utils';
import {
  averageAllocationPct,
  calculateResourceAllocation,
  EMPTY_ALLOCATION_CAPACITY,
} from '@/utils/allocation-utilisation';
import {
  RAW_TABLE_CONTAINER_CLASS,
  RAW_TABLE_SCROLL_CLASS,
  TABLE_HEADER_ROW_CLASS,
  RAW_TABLE_CLASS,
  TABLE_STRIPE_BG,
  TABLE_HOVER_BG,
  alternatingRowBackground,
} from '@/lib/table-styles';

// ─── Colour token resolvers ────────────────────────────────────────────────────

function resolveToken(token: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  return v || fallback;
}



// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const MIN_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];
const DEFAULT_PAGE_SIZE = 25;

const DEFAULT_WORKING_PROFILE: WorkingHoursProfile = {
  monday: 8, tuesday: 8, wednesday: 8, thursday: 8, friday: 8,
  saturday: 0, sunday: 0,
};

const DAY_KEYS: (keyof WorkingHoursProfile)[] = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

// ─── Types ────────────────────────────────────────────────────────────────────

type PeriodKey = 'week' | 'month' | 'quarter' | 'rolling12';
type ViewMode = 'resource' | 'project';
type BookingTypeFilter = 'both' | 'confirmed' | 'planned';
type ViewBy = 'individual' | 'team' | 'department' | 'location' | 'role';

export interface AllocationBooking {
  id: string;
  resourceId: string;
  resourceName: string;
  resourceRole: string;
  resourceDept: string;
  resourceInitials: string;
  resourcePhotoUrl?: string;
  projectId: string;
  projectName: string;
  clientName: string;
  bookingType: 'confirmed' | 'planned';
  startDate: Date;
  endDate: Date;
  startDateStr: string;
  endDateStr: string;
  /** Original booking timing, carried through for time-aware conflict detection. */
  startTime: string;
  endTime: string;
  durationType: DurationType;
  halfDayPeriod?: HalfDayPeriod;
  allocationPct: number;
  hours: number;
  hasConflict: boolean;
  status: 'confirmed' | 'planned';
}

export interface ResourceGroup {
  resourceId: string;
  resourceName: string;
  resourceRole: string;
  resourceDept: string;
  resourceInitials: string;
  resourcePhotoUrl?: string;
  totalAllocationPct: number;
  /** Booked hours in the report period */
  bookedHours: number;
  /** Working-profile hours in the period, less bank holidays and absences */
  capacityHours: number;
  bookings: AllocationBooking[];
  hasConflict: boolean;
}

export interface ProjectGroup {
  projectId: string;
  projectName: string;
  clientName: string;
  startDate: Date | null;
  endDate: Date | null;
  startDateStr: string;
  endDateStr: string;
  totalResources: number;
  staffingStatus: 'fully-staffed' | 'partially-staffed' | 'unfilled-roles' | 'overstaffed' | 'planned';
  unfilledRoleCount: number;
  bookings: AllocationBooking[];
  unfilledRoles: string[];
}

/** Generic grouping for team / department / location / role views */
export interface GroupedView {
  groupKey: string;
  groupLabel: string;
  bookings: AllocationBooking[];
  resources: ResourceGroup[];
  avgAllocationPct: number;
  hasConflict: boolean;
  totalBookings: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = String(name ?? '').trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function profileHoursForDay(dayOfWeek: number, profile: WorkingHoursProfile): number {
  return profile[DAY_KEYS[dayOfWeek]] ?? 0;
}

function getEffectiveProfile(
  resource: Resource,
  globalProfile: WorkingHoursProfile,
): WorkingHoursProfile {
  if (isWorkingHoursProfileConfigured(resource.customWorkingProfile)) {
    return resource.customWorkingProfile;
  }
  return globalProfile;
}

function workingHoursInInterval(start: Date, end: Date, profile: WorkingHoursProfile): number {
  let total = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    total += profileHoursForDay(cursor.getDay(), profile);
    cursor.setDate(cursor.getDate() + 1);
  }
  return total;
}

function durationFactor(durationType: Booking['durationType']): number {
  switch (durationType) {
    case 'half-day':
    case 'half-day-am':
    case 'half-day-pm':
      return 0.5;
    default:
      return 1;
  }
}

function bookingHoursInRange(booking: Booking, rangeStart: Date, rangeEnd: Date, profile: WorkingHoursProfile): number {
  const bStart = parseISO(booking.startTime);
  let bEnd     = parseISO(booking.endTime);

  // For day-based bookings where endTime <= startTime (e.g. single-day
  // bookings stored with the same timestamp for start and end), treat the
  // booking as spanning at least one full day.
  if (booking.durationType !== 'hourly' && bEnd.getTime() <= bStart.getTime()) {
    bEnd = addDays(startOfDay(bStart), 1);
  }

  const overlapStart = dateMax([bStart, rangeStart]);
  const overlapEnd   = dateMin([bEnd,   rangeEnd]);
  if (overlapStart >= overlapEnd) return 0;

  // Hourly bookings: derive total hours from the start/end time span,
  // then pro-rate for partial overlap with the date range.
  if (booking.durationType === 'hourly') {
    const bookingMs = bEnd.getTime() - bStart.getTime();
    if (bookingMs > 0) {
      const totalHours = bookingMs / (1000 * 60 * 60);
      const overlapMs  = overlapEnd.getTime() - overlapStart.getTime();
      return totalHours * (overlapMs / bookingMs);
    }
    // Genuine zero-hour hourly booking (start == end) contributes nothing —
    // never fall through to the day-based full-day calculation below.
    return 0;
  }

  // Day-based bookings: subtract 1 ms so the working-day count treats the
  // end as inclusive of the last booked day rather than spilling into the
  // following day.
  const inclusiveEnd = new Date(overlapEnd.getTime() - 1);
  if (inclusiveEnd < overlapStart) return 0;
  const hours = workingHoursInInterval(overlapStart, inclusiveEnd, profile);
  return hours * durationFactor(booking.durationType);
}

function computeDateRange(p: PeriodKey): { from: Date; to: Date } {
  const today = new Date();
  switch (p) {
    case 'week':
      return {
        from: startOfWeek(today, { weekStartsOn: 1 }),
        to:   endOfWeek(today,   { weekStartsOn: 1 }),
      };
    case 'month':
      return { from: startOfMonth(today), to: endOfMonth(today) };
    case 'quarter':
      return { from: startOfQuarter(today), to: endOfQuarter(today) };
    case 'rolling12':
      return { from: subMonths(today, 12), to: today };
  }
}

/**
 * End date implied by a start date for the selected period, so the range always
 * spans exactly one period from whatever start date the user picks.
 */
function endDateForPeriod(p: PeriodKey, from: Date): Date {
  const start = startOfDay(from);
  switch (p) {
    case 'week':
      return addDays(start, 6);
    case 'month':
      return addDays(addMonths(start, 1), -1);
    case 'quarter':
      return addDays(addQuarters(start, 1), -1);
    case 'rolling12':
      return addDays(addMonths(start, 12), -1);
  }
}

function stepPeriod(p: PeriodKey, direction: 1 | -1, currentFrom: Date): { from: Date; to: Date } {
  switch (p) {
    case 'week': {
      const ref = direction === 1 ? addWeeks(currentFrom, 1) : subWeeks(currentFrom, 1);
      return { from: startOfWeek(ref, { weekStartsOn: 1 }), to: endOfWeek(ref, { weekStartsOn: 1 }) };
    }
    case 'month': {
      const ref = direction === 1 ? addMonths(currentFrom, 1) : subMonths(currentFrom, 1);
      return { from: startOfMonth(ref), to: endOfMonth(ref) };
    }
    case 'quarter': {
      const ref = direction === 1 ? addQuarters(currentFrom, 1) : subQuarters(currentFrom, 1);
      return { from: startOfQuarter(ref), to: endOfQuarter(ref) };
    }
    case 'rolling12': {
      const ref = direction === 1 ? addMonths(currentFrom, 1) : subMonths(currentFrom, 1);
      return { from: ref, to: addMonths(ref, 12) };
    }
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// Segmented toggle control
interface SegmentedProps<T extends string> {
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
}
function Segmented<T extends string>({ options, value, onChange }: SegmentedProps<T>) {
  return (
    <div className="inline-flex items-center gap-1.5 shrink-0">
      {options.map((o) => (
        <Button
          key={o.value}
          type="button"
          variant={o.value === value ? 'default' : 'outline'}
          size="sm"
          className="h-8"
          onClick={() => onChange(o.value)}
          aria-pressed={o.value === value}
        >
          {o.label}
        </Button>
      ))}
    </div>
  );
}

// Sortable column header
interface SortHeaderProps {
  label: string;
  col: string;
  sortCol: string | null;
  sortDir: 'asc' | 'desc';
  onSort: (col: string) => void;
  align?: 'left' | 'right';
  minWidth?: number;
}
function SortHeader({ label, col, sortCol, sortDir, onSort, align = 'left', minWidth }: SortHeaderProps) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className="cursor-pointer select-none px-3 py-2.5 font-medium text-[12px] text-muted-foreground whitespace-nowrap group"
      style={{ textAlign: align, minWidth }}
    >
      <span
        className="inline-flex items-center gap-1"
        style={{ justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}
      >
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ChevronUp className="h-3 w-3 text-foreground" />
          ) : (
            <ChevronDown className="h-3 w-3 text-foreground" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-40 group-hover:opacity-70 transition-opacity" />
        )}
      </span>
    </th>
  );
}

// Booking type pill
function BookingTypePill({ type }: { type: 'confirmed' | 'planned' }) {
  if (type === 'confirmed') {
    return (
      <Badge variant="success" accent="success" className="text-[10px] px-1.5 py-0 capitalize whitespace-nowrap">
        Confirmed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground capitalize whitespace-nowrap">
      Planned
    </Badge>
  );
}

// Mixed booking type pill (for project view consolidated rows)
function MixedPill() {
  return (
    <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-muted text-muted-foreground border-border capitalize whitespace-nowrap">
      Mixed
    </Badge>
  );
}

// Conflict flag cell
function ConflictFlag({
  hasConflict,
  onClick,
}: {
  hasConflict: boolean;
  onClick?: () => void;
}) {
  if (!hasConflict) return <span className="text-muted-foreground text-[12px]">—</span>;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className="inline-flex items-center gap-1 text-destructive text-[11px] font-medium hover:underline cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      title="Click to view conflict details"
    >
      <span className="inline-block h-2 w-2 rounded-full bg-destructive shrink-0" />
      Conflict
    </button>
  );
}

// Staffing status badge
function StaffingBadge({ status, unfilledRoleCount = 0 }: { status: ProjectGroup['staffingStatus']; unfilledRoleCount?: number }) {
  switch (status) {
    case 'fully-staffed':
      return <Badge variant="success" accent="success" className="text-[10px] px-1.5 py-0">Fully staffed</Badge>;
    case 'partially-staffed':
      return <Badge variant="warning" accent="warning" className="text-[10px] px-1.5 py-0">Partially staffed</Badge>;
    case 'unfilled-roles':
      return (
        <Badge variant="destructive" accent="destructive" className="text-[10px] px-1.5 py-0">
          {unfilledRoleCount > 0 ? `${unfilledRoleCount} unfilled role${unfilledRoleCount !== 1 ? 's' : ''}` : 'Unfilled roles'}
        </Badge>
      );
    case 'overstaffed':
      return <Badge variant="warning" accent="warning" className="text-[10px] px-1.5 py-0">Overstaffed</Badge>;
    case 'planned':
      return <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">Planned</Badge>;
    default:
      return <Badge variant="success" accent="success" className="text-[10px] px-1.5 py-0">Fully staffed</Badge>;
  }
}

// Allocation % cell with colour
function AllocationCell({ pct, forceRed = false }: { pct: number; forceRed?: boolean }) {
  let colour = 'text-foreground';
  if (forceRed || pct > 100) colour = 'text-destructive';
  else if (pct >= 80)        colour = 'text-[var(--teal-600,oklch(0.45_0.08_194))]';
  else if (pct >= 50)        colour = 'text-foreground';
  else                       colour = 'text-muted-foreground';
  return <span className={`${colour} font-medium`}>{pct}%</span>;
}

// Summary stat card — same style as UtilisationReport (no left border accent)
interface StatCardProps {
  value: string | number;
  label: string;
  subLabel: string;
  valueColour?: 'default' | 'teal' | 'destructive' | 'warning';
  isFilterable?: boolean;
  isActive?: boolean;
  onClick?: () => void;
}
function StatCard({
  value,
  label,
  subLabel,
  valueColour = 'default',
  isFilterable,
  isActive,
  onClick,
}: StatCardProps) {
  const colourClass =
    valueColour === 'teal'       ? 'text-[var(--accent-teal-900,oklch(0.30_0.07_194))] dark:text-[var(--teal-400)]'
    : valueColour === 'destructive' ? 'text-destructive'
    : valueColour === 'warning'  ? 'text-warning-500'
    : 'text-foreground';

  return (
    <div
      className={[
        'flex-1 flex flex-col gap-1 rounded-[var(--radius-md)] border bg-card px-4 py-4 min-w-0 transition-all duration-150',
        isFilterable ? 'cursor-pointer select-none hover:border-primary/50 hover:shadow-sm' : '',
        isActive ? 'border-primary ring-2 ring-primary/20 shadow-sm' : 'border-border',
      ].join(' ')}
      role={isFilterable ? 'button' : undefined}
      tabIndex={isFilterable ? 0 : undefined}
      aria-pressed={isFilterable ? isActive : undefined}
      onClick={isFilterable ? onClick : undefined}
      onKeyDown={isFilterable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } } : undefined}
    >
      <div className="flex items-start justify-between gap-1">
        <span className={`text-2xl font-semibold leading-none ${colourClass}`}>
          {value}
        </span>
        {isFilterable && isActive && (
          <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary/10 text-primary shrink-0 mt-0.5">
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </span>
        )}
      </div>
      <span className="text-xs text-muted-foreground mt-1">{label}</span>
      <span className="text-xs text-muted-foreground">{subLabel}</span>
      {isFilterable && !isActive && (
        <span className="text-[10px] text-primary/70 mt-1 font-medium">Click to filter ↓</span>
      )}
      {isFilterable && isActive && (
        <span className="text-[10px] text-primary mt-1 font-medium">Filtering active · click to clear</span>
      )}
    </div>
  );
}

// ─── Resource detail side drawer ──────────────────────────────────────────────

interface ResourceDrawerProps {
  resourceGroup: ResourceGroup | null;
  open: boolean;
  onClose: () => void;
  onNavigateToCalendar?: () => void;
  onConflictClick?: (resourceId: string) => void;
  onResolveConflict?: (resourceId: string) => void;
}

function ResourceDrawer({
  resourceGroup,
  open,
  onClose,
  onNavigateToCalendar,
  onConflictClick,
  onResolveConflict,
}: ResourceDrawerProps) {
  if (!resourceGroup) return null;

  const rg = resourceGroup;
  const totalHours = rg.bookedHours;
  // Working-profile hours for the period, less bank holidays and absences.
  const availableHours = rg.capacityHours;
  const utilisedHours  = rg.bookings
    .filter((b) => b.bookingType === 'confirmed')
    .reduce((s, b) => s + b.hours, 0);
  const plannedHours   = rg.bookings
    .filter((b) => b.bookingType === 'planned')
    .reduce((s, b) => s + b.hours, 0);

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="p-0 flex flex-col gap-0 overflow-hidden w-full sm:max-w-[520px]"
      >
        <SheetTitle className="sr-only">{rg.resourceName} — Resource Allocation Detail</SheetTitle>
        <SheetDescription className="sr-only">
          Booking list, utilisation breakdown and conflict details for {rg.resourceName}.
        </SheetDescription>

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar className="h-11 w-11 shrink-0">
              {rg.resourcePhotoUrl && <img src={rg.resourcePhotoUrl} alt={rg.resourceName} />}
              <AvatarFallback className="text-sm font-semibold bg-primary/10 text-primary">
                {rg.resourceInitials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-semibold text-sm text-foreground truncate">{rg.resourceName}</p>
              <p className="text-xs text-muted-foreground truncate">{rg.resourceRole}</p>
              <p className="text-xs text-muted-foreground truncate">{rg.resourceDept}</p>
            </div>
          </div>
          <div className="shrink-0 ml-3 text-right">
            <p
              className={`text-2xl font-semibold leading-none ${
                rg.totalAllocationPct > 100
                  ? 'text-destructive'
                  : rg.totalAllocationPct >= 80
                  ? 'text-[var(--teal-600,oklch(0.45_0.08_194))]'
                  : 'text-foreground'
              }`}
            >
              {rg.totalAllocationPct}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">Allocation</p>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">

          {/* Conflict alert */}
          {rg.hasConflict && (
            <div
              className="mx-5 mt-4 rounded-md px-3 py-2.5 flex items-start gap-2"
              style={{
                background: 'var(--accent-bg-outline, #fee2e2)',
                border: '1px solid var(--accent-border, #fca5a5)',
              }}
            >
              <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-destructive">Booking conflict</p>
                <p className="text-xs text-destructive/80 mt-0.5">
                  This resource has overlapping bookings in the selected period.
                </p>
                {onConflictClick && (
                  <button
                    onClick={() => onConflictClick(rg.resourceId)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-destructive underline underline-offset-2 hover:no-underline mt-1"
                  >
                    View conflict details
                    <ExternalLink className="h-3 w-3" />
                  </button>
                )}
              </div>
              {onResolveConflict && (
                <button
                  onClick={(e) => { e.stopPropagation(); onResolveConflict(rg.resourceId); }}
                  className="shrink-0 self-center inline-flex items-center h-7 px-2.5 rounded border border-destructive text-destructive text-[12px] font-medium bg-transparent hover:bg-destructive/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/50 whitespace-nowrap"
                >
                  Resolve conflict ›
                </button>
              )}
            </div>
          )}

          {/* Utilisation breakdown */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">Utilisation breakdown</p>
            <div className="flex flex-col gap-3">
              {/* Confirmed bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Confirmed bookings</span>
                  <span className="font-semibold text-foreground">{utilisedHours}h</span>
                </div>
                <Progress
                  value={availableHours > 0 ? Math.min((utilisedHours / availableHours) * 100, 100) : 0}
                  className="h-2"
                  style={{ background: 'var(--muted)' } as React.CSSProperties}
                />
              </div>
              {/* Planned bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Planned bookings</span>
                  <span className="font-semibold text-muted-foreground">{plannedHours}h</span>
                </div>
                <Progress
                  value={availableHours > 0 ? Math.min((plannedHours / availableHours) * 100, 100) : 0}
                  className="h-2"
                  style={{ background: 'var(--muted)' } as React.CSSProperties}
                />
              </div>
              {/* Chips */}
              <div className="flex gap-2 mt-1">
                {[
                  { label: 'Confirmed', value: `${utilisedHours}h` },
                  { label: 'Planned',   value: `${plannedHours}h` },
                  { label: 'Available', value: `${Math.max(0, availableHours - totalHours)}h` },
                ].map((chip) => (
                  <div
                    key={chip.label}
                    className="flex-1 rounded-md px-2.5 py-2 flex flex-col gap-0.5 text-center bg-muted border border-border"
                  >
                    <span className="text-sm font-semibold text-foreground">{chip.value}</span>
                    <span className="text-xs text-muted-foreground">{chip.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Booking list */}
          <div className="px-5 py-4 border-b">
            <p className="text-xs font-semibold text-foreground mb-3">
              Bookings in period ({rg.bookings.length})
            </p>
            {rg.bookings.length === 0 ? (
              <p className="text-xs text-muted-foreground">No bookings in this period.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {rg.bookings.map((b) => (
                  <div
                    key={b.id}
                    className="flex items-start justify-between gap-2 rounded-md border border-border px-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{b.projectName}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {b.startDateStr} – {b.endDateStr}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">
                          {b.allocationPct}% · {b.hours}h
                        </span>
                        {b.hasConflict && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-destructive font-medium">
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-destructive" />
                            Conflict
                          </span>
                        )}
                      </div>
                    </div>
                    <BookingTypePill type={b.bookingType} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Navigate to calendar */}
          {onNavigateToCalendar && (
            <div className="px-5 py-4">
              <button
                onClick={onNavigateToCalendar}
                className="inline-flex items-center gap-1.5 text-[13px] text-primary hover:underline font-medium"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View in calendar planner
              </button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
      <div
        className="flex items-center justify-center h-14 w-14 rounded-2xl"
        style={{ background: 'var(--muted)' }}
      >
        <BarChart2 className="h-7 w-7 text-muted-foreground" />
      </div>
      <p className="text-[15px] font-medium text-foreground">
        No bookings found for the selected period and filters
      </p>
      <p className="text-[13px] text-muted-foreground max-w-xs">
        Try adjusting the date range or clearing filters to see allocations.
      </p>
      <button
        onClick={onReset}
        className="text-[13px] text-primary hover:underline mt-1 cursor-pointer"
      >
        Reset filters
      </button>
    </div>
  );
}

// ─── Data hook ────────────────────────────────────────────────────────────────

function useAllocationData(
  dateFrom: Date,
  dateTo: Date,
  bookingTypeFilter: BookingTypeFilter,
  selectedResourceIds: string[],
  selectedDepartments: string[],
  selectedTeams: string[],
  selectedProjects: string[],
  selectedRoles: string[],
  conflictsOnly: boolean,
  viewBy: ViewBy,
  resources: Resource[],
  bookings: Booking[],
  settings: Record<string, unknown>,
  absences: Absence[],
  bankHolidays: BankHoliday[],
) {

  return useMemo(() => {
    // Real resources only
    const realResources = resources.filter(
      (r) => r.id !== 'unallocated' && r.status !== 'inactive',
    );

    // Apply resource / department / team / role filters
    const filteredResources = realResources.filter((r) => {
      if (selectedResourceIds.length > 0 && !selectedResourceIds.includes(r.id)) return false;
      if (selectedDepartments.length > 0 && !selectedDepartments.includes(r.department ?? '')) return false;
      if (selectedTeams.length > 0 && !selectedTeams.includes(r.schedulerGroup ?? '')) return false;
      if (selectedRoles.length > 0 && !selectedRoles.includes(r.role ?? '')) return false;
      return true;
    });

    // Bucket bookings by resourceId
    const bookingsByResource: Record<string, Booking[]> = {};
    for (const b of bookings) {
      const rid = b.resourceId ?? '';
      if (!rid || rid === 'unallocated') continue;
      if (!bookingsByResource[rid]) bookingsByResource[rid] = [];
      bookingsByResource[rid].push(b);
    }

    // Resolve global working profile from settings
    const globalProfile: WorkingHoursProfile =
      (settings as unknown as AppSettings).globalWorkingProfile ?? DEFAULT_WORKING_PROFILE;

    // Build a map of resource id → effective working profile
    const resourceProfileMap = new Map<string, WorkingHoursProfile>();
    for (const r of filteredResources) {
      resourceProfileMap.set(r.id, getEffectiveProfile(r, globalProfile));
    }

    const bookingTypes = (settings as unknown as AppSettings).bookingTypes;

    // ── Build AllocationBookings ──────────────────────────────────────────────
    const allBookings: AllocationBooking[] = [];
    /** Report booking id → the booking it was derived from */
    const sourceBookingById = new Map<string, Booking>();

    for (const r of filteredResources) {
      const profile = resourceProfileMap.get(r.id)!;
      const resourceBookings = (bookingsByResource[r.id] ?? []).filter((b) => {
        if (b.status === 'cancelled') return false;
        const bStart = parseISO(b.startTime);
        const bEnd   = parseISO(b.endTime);
        if (bStart >= dateTo || bEnd <= dateFrom) return false;
        // Booking type filter
        const isConfirmed = b.status === 'confirmed' || b.status === 'completed';
        if (bookingTypeFilter === 'confirmed' && !isConfirmed) return false;
        if (bookingTypeFilter === 'planned' && isConfirmed) return false;
        // Project filter
        if (selectedProjects.length > 0) {
          const pid = String(b.projectId ?? b.projectName);
          if (!selectedProjects.includes(pid)) return false;
        }
        return true;
      });

      for (const b of resourceBookings) {
        const bStart = parseISO(b.startTime);
        let bEnd     = parseISO(b.endTime);

        // For day-based bookings where endTime <= startTime, treat as one day.
        if (b.durationType !== 'hourly' && bEnd.getTime() <= bStart.getTime()) {
          bEnd = addDays(startOfDay(bStart), 1);
        }

        const hrs = bookingHoursInRange(b, dateFrom, dateTo, profile);
        if (hrs <= 0) continue;

        const isConfirmed = b.status === 'confirmed' || b.status === 'completed';

        // Compute booking capacity using the same boundary logic as bookingHoursInRange.
        let bookingCapacity: number;
        if (b.durationType === 'hourly') {
          bookingCapacity = (bEnd.getTime() - bStart.getTime()) / (1000 * 60 * 60);
        } else {
          const capacityEnd = new Date(bEnd.getTime() - 1);
          bookingCapacity = capacityEnd >= bStart
            ? workingHoursInInterval(bStart, capacityEnd, profile) * durationFactor(b.durationType)
            : 0;
        }
        const allocationPct = bookingCapacity > 0
          ? Math.round((hrs / bookingCapacity) * 100)
          : 0;

        allBookings.push({
          id:              b.id,
          resourceId:      r.id,
          resourceName:    r.name,
          resourceRole:    r.role ?? '',
          resourceDept:    r.department ?? r.schedulerGroup ?? '—',
          resourceInitials: getInitials(r.name),
          resourcePhotoUrl: r.photoUrl,
          projectId:       String(b.projectId ?? b.projectName),
          projectName:     b.projectName,
          clientName:      '—', // client name not directly on booking
          bookingType:     isConfirmed ? 'confirmed' : 'planned',
          startDate:       bStart,
          endDate:         bEnd,
          startDateStr:    format(bStart, 'dd MMM yyyy'),
          endDateStr:      format(bEnd, 'dd MMM yyyy'),
          startTime:       b.startTime,
          endTime:         b.endTime,
          durationType:    b.durationType,
          halfDayPeriod:   b.halfDayPeriod,
          allocationPct,
          hours:           hrs,
          hasConflict:     false, // computed below
          status:          isConfirmed ? 'confirmed' : 'planned',
        });
        sourceBookingById.set(b.id, b);
      }
    }

    // ── Detect conflicts (overlapping bookings for same resource) ─────────────
    // Only mark specific booking rows that are part of an actual overlap pair.
    const resourceIdSet = new Set(allBookings.map((b) => b.resourceId));
    const conflictResourceIds = new Set<string>();
    // Track which specific booking IDs are in a conflict pair
    const conflictBookingIds = new Set<string>();

    for (const rid of resourceIdSet) {
      const rBkgs = allBookings
        .filter((b) => b.resourceId === rid)
        .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

      for (let i = 0; i < rBkgs.length; i++) {
        for (let j = i + 1; j < rBkgs.length; j++) {
          // Genuine same-day, time-aware overlap (honours full-day vs
          // half-day-AM/PM vs hourly rules) — a long multi-day full-day
          // booking is only flagged against bookings it actually shares a day
          // and minute-of-day interval with.
          if (bookingSegmentsOverlap(rBkgs[i], rBkgs[j])) {
            conflictResourceIds.add(rid);
            conflictBookingIds.add(rBkgs[i].id);
            conflictBookingIds.add(rBkgs[j].id);
          }
        }
      }
    }

    // Mark only overlapping booking rows with conflict flag
    for (const b of allBookings) {
      if (conflictBookingIds.has(b.id)) {
        b.hasConflict = true;
      }
    }

    // ── Apply conflict-only filter ────────────────────────────────────────────
    const visibleBookings = conflictsOnly
      ? allBookings.filter((b) => b.hasConflict)
      : allBookings;

    // ── Build resource groups ─────────────────────────────────────────────────
    const resourceGroupMap = new Map<string, ResourceGroup>();

    for (const b of visibleBookings) {
      if (!resourceGroupMap.has(b.resourceId)) {
        resourceGroupMap.set(b.resourceId, {
          resourceId:      b.resourceId,
          resourceName:    b.resourceName,
          resourceRole:    b.resourceRole,
          resourceDept:    b.resourceDept,
          resourceInitials: b.resourceInitials,
          resourcePhotoUrl: b.resourcePhotoUrl,
          totalAllocationPct: 0,
          bookedHours:     0,
          capacityHours:   0,
          bookings:        [],
          hasConflict:     false,
        });
      }
      const rg = resourceGroupMap.get(b.resourceId)!;
      rg.bookings.push(b);
      if (b.hasConflict) rg.hasConflict = true;
    }

    // Map resourceId → resource for quick lookup
    const resourceById = new Map(filteredResources.map((r) => [r.id, r]));

    // Allocation % per resource: booked hours over the working-profile hours
    // for the period, less the hours removed by bank holidays and absences —
    // the same capacity rules the planner's utilisation badge uses.
    for (const rg of resourceGroupMap.values()) {
      const resource = resourceById.get(rg.resourceId);
      const sourceBookings = rg.bookings
        .map((b) => sourceBookingById.get(b.id))
        .filter((b): b is Booking => Boolean(b));
      const allocation = resource
        ? calculateResourceAllocation(
            resource,
            dateFrom,
            dateTo,
            sourceBookings,
            globalProfile,
            bankHolidays,
            absences,
            bookingTypes,
          )
        : EMPTY_ALLOCATION_CAPACITY;
      rg.bookedHours        = allocation.bookedHours;
      rg.capacityHours      = allocation.capacityHours;
      rg.totalAllocationPct = allocation.allocationPct;
    }

    const resourceGroups = Array.from(resourceGroupMap.values()).sort((a, b) =>
      a.resourceName.localeCompare(b.resourceName),
    );

    // Unallocated resources (in period, no bookings, not filtered out)
    const allocatedResourceIds = new Set(allBookings.map((b) => b.resourceId));
    const unallocatedResources = filteredResources.filter(
      (r) => !allocatedResourceIds.has(r.id),
    );

    // ── Build project groups ──────────────────────────────────────────────────
    const projectGroupMap = new Map<string, ProjectGroup>();

    for (const b of visibleBookings) {
      if (!projectGroupMap.has(b.projectId)) {
        projectGroupMap.set(b.projectId, {
          projectId:       b.projectId,
          projectName:     b.projectName,
          clientName:      b.clientName,
          startDate:       null,
          endDate:         null,
          startDateStr:    '',
          endDateStr:      '',
          totalResources:  0,
          staffingStatus:  'fully-staffed',
          unfilledRoleCount: 0,
          bookings:        [],
          unfilledRoles:   [],
        });
      }
      const pg = projectGroupMap.get(b.projectId)!;
      pg.bookings.push(b);
      if (!pg.startDate || b.startDate < pg.startDate) pg.startDate = b.startDate;
      if (!pg.endDate || b.endDate > pg.endDate) pg.endDate = b.endDate;
    }

    for (const pg of projectGroupMap.values()) {
      const uniqueResources = new Set(pg.bookings.map((b) => b.resourceId));
      pg.totalResources = uniqueResources.size;
      pg.startDateStr = pg.startDate ? format(pg.startDate, 'dd MMM yyyy') : '—';
      pg.endDateStr   = pg.endDate   ? format(pg.endDate,   'dd MMM yyyy') : '—';
      pg.unfilledRoleCount = 0;

      // ── Compute staffing status ──────────────────────────────────────────
      // Check if any resource on this project is over 100% allocated (overstaffed)
      const resourceTotalPct: Record<string, number> = {};
      for (const b of pg.bookings) {
        resourceTotalPct[b.resourceId] = (resourceTotalPct[b.resourceId] ?? 0) + b.allocationPct;
      }
      const isOverstaffed = Object.values(resourceTotalPct).some((pct) => pct > 100);

      // No resources booked at all → unfilled
      if (pg.totalResources === 0) {
        pg.staffingStatus = 'unfilled-roles';
        pg.unfilledRoleCount = 1;
      } else if (isOverstaffed) {
        pg.staffingStatus = 'overstaffed';
      } else {
        const hasConfirmed = pg.bookings.some((b) => b.bookingType === 'confirmed');
        const hasPlanned   = pg.bookings.some((b) => b.bookingType === 'planned');

        if (hasConfirmed && !hasPlanned) {
          // All confirmed
          pg.staffingStatus = 'fully-staffed';
        } else if (!hasConfirmed && hasPlanned) {
          // All planned, no confirmed
          pg.staffingStatus = 'planned';
        } else if (hasConfirmed && hasPlanned) {
          // Mix of confirmed and planned
          pg.staffingStatus = 'partially-staffed';
        } else {
          pg.staffingStatus = 'fully-staffed';
        }
      }
    }

    const projectGroups = Array.from(projectGroupMap.values()).sort((a, b) =>
      a.projectName.localeCompare(b.projectName),
    );

    // ── Summary stats ─────────────────────────────────────────────────────────
    const totalBookings        = visibleBookings.length;
    const resourcesBooked      = resourceGroupMap.size;
    const resourcesWithConflicts = conflictResourceIds.size;
    // Average allocation across every resource in the period:
    // Σ booked hours / Σ capacity hours × 100.
    const avgAllocationPct = averageAllocationPct(Array.from(resourceGroupMap.values()));
    const unallocatedCount = conflictsOnly ? 0 : unallocatedResources.length;

    const isEmpty = visibleBookings.length === 0;

    // ── Build grouped views (team / department / location / role) ─────────────
    const buildGroupedView = (getKey: (b: AllocationBooking) => string, getLabel?: (k: string) => string): GroupedView[] => {
      const groupMap = new Map<string, GroupedView>();
      for (const b of visibleBookings) {
        const key = getKey(b) || '—';
        if (!groupMap.has(key)) {
          groupMap.set(key, {
            groupKey:     key,
            groupLabel:   getLabel ? getLabel(key) : key,
            bookings:     [],
            resources:    [],
            avgAllocationPct: 0,
            hasConflict:  false,
            totalBookings: 0,
          });
        }
        const g = groupMap.get(key)!;
        g.bookings.push(b);
        if (b.hasConflict) g.hasConflict = true;
      }
      // Attach resource groups and compute avg allocation
      for (const g of groupMap.values()) {
        g.totalBookings = g.bookings.length;
        const rgIds = new Set(g.bookings.map((b) => b.resourceId));
        g.resources = resourceGroups.filter((rg) => rgIds.has(rg.resourceId));
        g.avgAllocationPct = averageAllocationPct(g.resources);
      }
      return Array.from(groupMap.values()).sort((a, b) => a.groupLabel.localeCompare(b.groupLabel));
    };

    const groupedByTeam       = buildGroupedView((b) => resourceById.get(b.resourceId)?.schedulerGroup ?? '—');
    const groupedByDepartment = buildGroupedView((b) => resourceById.get(b.resourceId)?.department ?? '—');
    const groupedByLocation   = buildGroupedView((b) => (resourceById.get(b.resourceId) as unknown as Record<string, string>)?.location ?? '—');
    const groupedByRole       = buildGroupedView((b) => b.resourceRole || '—');

    const groupedViews: Record<string, GroupedView[]> = {
      team:       groupedByTeam,
      department: groupedByDepartment,
      location:   groupedByLocation,
      role:       groupedByRole,
    };

    return {
      allBookings: visibleBookings,
      resourceGroups,
      projectGroups,
      conflictResourceIds,
      unallocatedResources,
      groupedViews,
      summary: {
        totalBookings,
        resourcesBooked,
        resourcesWithConflicts,
        avgAllocationPct,
        unallocatedCount,
      },
      isEmpty,
    };
  }, [
    resources,
    bookings,
    settings,
    dateFrom,
    dateTo,
    bookingTypeFilter,
    selectedResourceIds,
    selectedDepartments,
    selectedTeams,
    selectedProjects,
    selectedRoles,
    conflictsOnly,
    viewBy,
    absences,
    bankHolidays,
  ]);
}

// ─── Main component ───────────────────────────────────────────────────────────

interface ResourceAllocationProps {
  /** Optional navigation callback — passed from App.tsx to allow cross-page jumps */
  onNavigate?: (page: string) => void;
  /** Opens the Calendar Planner pre-set to the report's period and date range */
  onNavigateToCalendarWithRange?: (ctx: PlannerNavContext) => void;
}

/** Report period → planner view shown when opening the Calendar Planner */
const PERIOD_TO_CALENDAR_VIEW: Record<PeriodKey, PlannerNavContext['view']> = {
  week: 'week',
  month: 'month',
  quarter: 'quarter',
  rolling12: 'year',
};

export function ResourceAllocation({ onNavigate, onNavigateToCalendarWithRange }: ResourceAllocationProps = {}) {
  const { canExportReport } = usePermissions();
  const canExport = canExportReport('resourceAllocationReport');

  // ── Data mode (useScreenData auto-switches between mockData and liveData)
  const {
    resources: allResources,
    bookings: contextBookings,
    settings,
    schedulerGroups,
    currentMode,
    isSettingsReady,
    updateBooking,
    addBooking,
    deleteBooking,
  } = useScreenData('calendar');

  // Scope visible resources by the role's report "View Access" (all / team / own)
  const { scopeResources } = useReportScope('resourceAllocationReport');
  const resources = useMemo(() => scopeResources(allResources), [scopeResources, allResources]);

  const { isLoading: isContextLoading } = useScheduler();
  const { currentCompany, isLoading: isCompanyLoading } = useCompany();
  const companyIdRef = useRef(currentCompany?.id);
  companyIdRef.current = currentCompany?.id;

  // ── Defaults
  const defaultPeriod: PeriodKey = 'month';
  const { from: defaultFrom, to: defaultTo } = computeDateRange(defaultPeriod);

  // ── Filter state
  const [period, setPeriod]                   = useState<PeriodKey>(defaultPeriod);
  const [dateFrom, setDateFrom]               = useState<Date>(defaultFrom);
  const [dateTo, setDateTo]                   = useState<Date>(defaultTo);

  // ── Live bookings (fetched from API for the report date range)
  // liveData.bookings is intentionally empty — bookings aren't bulk-loaded
  // in live mode, so we fetch them ourselves for the report's date range.
  const [liveReportBookings, setLiveReportBookings] = useState<Booking[]>([]);
  const [hasFetchedBookings, setHasFetchedBookings] = useState(false);
  const [liveBookingsError, setLiveBookingsError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fetchIdRef = useRef(0);

  const fetchLiveBookings = useCallback(async (start: Date, end: Date) => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    const id = ++fetchIdRef.current;
    setLiveBookingsError(null);
    try {
      const fetchEnd = addDays(end, 1);
      const apiBookings = await bookingsApi.getByDateRange(
        start.toISOString(),
        fetchEnd.toISOString(),
        false,
        companyIdRef.current,
      );
      if (abortRef.current?.signal.aborted) return;
      if (fetchIdRef.current === id) {
        const mapped = apiBookings.map(mapApiBookingToBooking);
        setLiveReportBookings(mapped);
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('Failed to fetch live bookings for resource allocation report:', message, err);
      if (fetchIdRef.current === id) {
        setLiveBookingsError(message);
        setLiveReportBookings([]);
      }
    } finally {
      if (fetchIdRef.current === id) setHasFetchedBookings(true);
    }
  }, []);

  useEffect(() => {
    if (currentMode !== 'live') {
      setHasFetchedBookings(false);
    }
  }, [currentMode]);

  useEffect(() => {
    if (currentMode !== 'live') return;
    if (isCompanyLoading || !currentCompany?.id) return;
    fetchLiveBookings(dateFrom, dateTo);
    return () => { abortRef.current?.abort(); };
  }, [currentMode, dateFrom, dateTo, fetchLiveBookings, currentCompany?.id, isCompanyLoading]);

  const bookings = currentMode === 'live' ? liveReportBookings : contextBookings;

  // ── Absences: they reduce a resource's capacity, exactly as they do in the
  // planner.  Fetched from the API in live mode, mock data otherwise.
  const [absences, setAbsences] = useState<Absence[]>(mockAbsences);

  useEffect(() => {
    if (currentMode !== 'live') {
      setAbsences(mockAbsences);
      return;
    }
    if (isCompanyLoading || !currentCompany?.id) return;

    let cancelled = false;
    absencesApi
      .getByDateRange(dateFrom.toISOString(), dateTo.toISOString(), currentCompany.id)
      .then((data) => { if (!cancelled) setAbsences(data); })
      .catch(() => { if (!cancelled) setAbsences([]); });
    return () => { cancelled = true; };
  }, [currentMode, dateFrom, dateTo, currentCompany?.id, isCompanyLoading]);

  // True while either resources or bookings are still loading in live mode.
  const isLiveDataLoading = currentMode === 'live' && (isContextLoading || !hasFetchedBookings);
  const [viewBy, setViewBy]                   = useState<ViewBy>('individual');
  const [bookingTypeFilter, setBookingTypeFilter] = useState<BookingTypeFilter>('both');
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams]     = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles]     = useState<string[]>([]);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // ── Draft filter state (applied only on "Apply Filters" click)
  const [draftResources, setDraftResources]     = useState<string[]>([]);
  const [draftDepartments, setDraftDepartments] = useState<string[]>([]);
  const [draftTeams, setDraftTeams]             = useState<string[]>([]);
  const [draftProjects, setDraftProjects]       = useState<string[]>([]);
  const [draftRoles, setDraftRoles]             = useState<string[]>([]);
  const [colConfigOpen, setColConfigOpen] = useState(false);
  const [conflictsOnly, setConflictsOnly]     = useState(false);
  const [showUnallocatedOnly, setShowUnallocatedOnly] = useState(false);

  // ── View mode
  const [viewMode, setViewMode] = useState<ViewMode>('resource');

  // ── Sort
  const [sortCol, setSortCol]   = useState<string | null>('resourceName');
  const [sortDir, setSortDir]   = useState<'asc' | 'desc'>('asc');

  // ── Pagination
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // ── Drawer
  const [drawerGroup, setDrawerGroup] = useState<ResourceGroup | null>(null);

  // ── Project view: expanded consolidated rows  key = `${projectId}-${resourceId}`
  const [expandedConsolidated, setExpandedConsolidated] = useState<Set<string>>(new Set());

  const toggleConsolidatedRow = useCallback((key: string) => {
    setExpandedConsolidated((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // ── Conflict resolution overlay
  const [conflictOverlaySuggestion, setConflictOverlaySuggestion] = useState<RebalancingSuggestion | null>(null);
  const [conflictOverlayItems, setConflictOverlayItems] = useState<ConflictItem[]>([]);

  // ── Period change
  const handlePeriodChange = useCallback((p: PeriodKey) => {
    const { from, to } = computeDateRange(p);
    setPeriod(p);
    setDateFrom(from);
    setDateTo(to);
    setPage(1);
  }, []);

  const handleStepPeriod = useCallback((direction: 1 | -1) => {
    const { from, to } = stepPeriod(period, direction, dateFrom);
    setDateFrom(from);
    setDateTo(to);
    setPage(1);
  }, [period, dateFrom]);

  const handleCustomDateFrom = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val) return;
    const d = parseISO(val);
    if (!isNaN(d.getTime())) {
      const from = startOfDay(d);
      setDateFrom(from);
      setDateTo(endDateForPeriod(period, from));
      setPage(1);
    }
  }, [period]);

  // ── Data
  const data = useAllocationData(
    dateFrom,
    dateTo,
    bookingTypeFilter,
    selectedResources,
    selectedDepartments,
    selectedTeams,
    selectedProjects,
    selectedRoles,
    conflictsOnly,
    viewBy,
    resources,
    bookings,
    settings as unknown as Record<string, unknown>,
    absences,
    mockBankHolidays,
  );

  // ── Planner hand-off: the report's period becomes the planner view, anchored
  // on today when the range covers it so the planner opens on the same period
  // the report is showing (a rolling-12 range maps to the current year, not the
  // year its start date happens to fall in).
  const openPlannerForReportRange = useCallback(() => {
    if (!onNavigateToCalendarWithRange) {
      toast.info('Navigate to the Calendar Planner view to see these changes.');
      return;
    }
    const today = startOfDay(new Date());
    const anchor = today >= startOfDay(dateFrom) && today <= startOfDay(dateTo) ? today : dateFrom;
    onNavigateToCalendarWithRange({
      view: PERIOD_TO_CALENDAR_VIEW[period],
      startDate: anchor,
      endDate: dateTo,
    });
  }, [onNavigateToCalendarWithRange, period, dateFrom, dateTo]);

  // ── Conflict resolution handler (depends on data, must come after it)
  const handleResolveConflict = useCallback((resourceId: string) => {
    const rg = data.resourceGroups.find((g) => g.resourceId === resourceId);
    if (!rg) return;

    const overlappingBookings = rg.bookings.filter((b) => b.hasConflict);

    // Keep one booking (the anchor) with the original resource and only offer
    // the surplus for reassignment.
    const { anchor, surplus: conflictBookings } = partitionConflictItems(
      overlappingBookings,
      (b) => ({
        id: String(b.id),
        startMs: b.startDate instanceof Date ? b.startDate.getTime() : Date.parse(String(b.startDate)),
        confirmed: b.status === 'confirmed',
      }),
    );
    const primaryBooking = anchor ?? conflictBookings[0];

    // Build per-booking conflict items (one entry per surplus booking)
    const items: ConflictItem[] = conflictBookings.map((b, idx) => ({
      id:           `${resourceId}-conflict-${idx}`,
      resourceId:   resourceId,
      resourceName: rg.resourceName,
      projectName:  b.projectName || '—',
      dateLabel:    b.startDateStr
        ? (() => {
            try {
              const d = new Date(b.startDate);
              return isNaN(d.getTime()) ? b.startDateStr : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
            } catch {
              return b.startDateStr;
            }
          })()
        : '—',
      startDate: b.startDate instanceof Date ? b.startDate.toISOString() : String(b.startDate),
    }));
    setConflictOverlayItems(items);

    const suggestion: RebalancingSuggestion = {
      atRiskResourceId:        resourceId,
      atRiskResourceName:      rg.resourceName,
      level:                   'critical',
      peakUtilisation:         rg.totalAllocationPct,
      trendSummary:            `${rg.resourceName} has overlapping bookings`,
      projectName:             primaryBooking?.projectName ?? '—',
      affectedWeekStarts:      [],
      candidateId:             '',
      candidateName:           '—',
      candidateRole:           rg.resourceRole,
      candidateAvgUtilisation: 0,
      sameRole:                false,
      sharedSkills:            [],
      candidateScore:          0,
      missingSkills:           [],
      totalAtRiskSkills:       0,
      // Only the surplus bookings move — keyed by exact id so the downstream
      // path never re-derives them by project name (which would sweep up the
      // anchor and unrelated same-project bookings).
      atRiskBookingIds:        conflictBookings.map((b) => String(b.id)),
    };
    setConflictOverlaySuggestion(suggestion);
  }, [data.resourceGroups]);

  // ── Commit conflict-resolution outcomes.
  // The overlay hands back the planned assignments (booking → target resource)
  // plus any in-place changes — reduced hours and delayed starts. Persist them
  // all, then refresh the report so the changes are reflected.
  const handleCommitReassignments = useCallback(
    async (applied: RebalancingSuggestion) => {
      const assignments = applied.plannedAssignments ?? [];

      // Reduce / delay keep the booking on its resource and only change its
      // hours or dates, so they are plain field updates.
      const inPlaceUpdates = new Map<string, Partial<Booking>>();
      [
        ...buildReductionMutations(applied.plannedReductions ?? [], contextBookings),
        ...buildDelayMutations(applied.plannedDelays ?? [], contextBookings),
      ].forEach((u) => {
        inPlaceUpdates.set(u.bookingId, { ...(inPlaceUpdates.get(u.bookingId) ?? {}), ...u.changes });
      });

      if (inPlaceUpdates.size > 0) {
        let inPlaceFailed = 0;
        await Promise.all(
          Array.from(inPlaceUpdates, async ([bookingId, changes]) => {
            try {
              await updateBooking(String(bookingId), changes);
            } catch {
              inPlaceFailed++;
            }
          }),
        );
        const changed = inPlaceUpdates.size - inPlaceFailed;
        if (changed > 0) {
          toast.success(`${changed} booking${changed > 1 ? 's' : ''} updated`);
        }
        if (inPlaceFailed > 0) {
          toast.error(
            `${inPlaceFailed} booking change${inPlaceFailed > 1 ? 's' : ''} failed`,
            { description: 'Please try again or edit those bookings manually.' },
          );
        }
      }

      if (assignments.length === 0) {
        if (inPlaceUpdates.size > 0 && currentMode === 'live') {
          await fetchLiveBookings(dateFrom, dateTo);
        }
        return;
      }

      // A whole booking is a simple resource update. A multi-day booking that
      // was split across resources becomes one per-day booking on each day's
      // target resource, with the original deleted — same helper the calendar
      // commit uses so both paths produce identical bookings.
      const { updates, creates, deletes } = buildReassignmentMutations(
        assignments,
        contextBookings,
        () => crypto.randomUUID(),
      );
      const totalOps = updates.length + creates.length + deletes.length;

      let failed = 0;
      await Promise.all(
        updates.map(async (u) => {
          try {
            await updateBooking(String(u.bookingId), { resourceId: String(u.resourceId) });
          } catch {
            failed++;
          }
        }),
      );
      await Promise.all(
        creates.map(async (c) => {
          try {
            await addBooking(c);
          } catch {
            failed++;
          }
        }),
      );
      await Promise.all(
        deletes.map(async (d) => {
          try {
            await deleteBooking(String(d));
          } catch {
            failed++;
          }
        }),
      );

      const movedCount = new Set(assignments.map((a) => a.bookingId)).size;
      if (failed < totalOps) {
        toast.success(
          `${movedCount} booking${movedCount > 1 ? 's' : ''} reassigned`,
        );
      }
      if (failed > 0) {
        toast.error(
          `${failed} reassignment${failed > 1 ? 's' : ''} failed`,
          { description: 'Please try again or edit those bookings manually.' },
        );
      }

      if (currentMode === 'live') {
        await fetchLiveBookings(dateFrom, dateTo);
      }
    },
    [updateBooking, addBooking, deleteBooking, contextBookings, currentMode, fetchLiveBookings, dateFrom, dateTo],
  );

  // ── Planner Group enabled (same rule as UtilisationReport)
  const teamsEnabled =
    settings.groupingMethod === 'scheduler-group' && schedulerGroups.length > 0;

  // Which grouping filter to render: `null` until company settings and
  // scheduler groups have resolved, so an unhydrated company never falls back
  // to showing Department.
  const groupingFilter: 'team' | 'department' | null = !isSettingsReady
    ? null
    : teamsEnabled
      ? 'team'
      : 'department';

  // ── Option lists
  const resourceOptions = useMemo(
    () =>
      resources
        .filter((r) => r.id !== 'unallocated')
        .map((r) => ({ value: r.id, label: r.name })),
    [resources],
  );

  const departmentOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated')
      .reduce<{ value: string; label: string }[]>((acc, r) => {
        const d = r.department ?? '';
        if (d && !seen.has(d)) { seen.add(d); acc.push({ value: d, label: d }); }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  const teamOptions = useMemo(() => {
    if (!teamsEnabled) return [];
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated' && r.schedulerGroup)
      .reduce<{ value: string; label: string }[]>((acc, r) => {
        const t = r.schedulerGroup!;
        if (!seen.has(t)) { seen.add(t); acc.push({ value: t, label: t }); }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources, teamsEnabled]);

  // Only the dimension matching the configured grouping method is available, so
  // the planner group view and any selection made against the other one is dropped.
  useEffect(() => {
    if (!isSettingsReady) return;
    if (teamsEnabled) {
      setSelectedDepartments([]);
      setDraftDepartments([]);
      setViewBy((prev) => (prev === 'department' ? 'team' : prev));
    } else {
      setSelectedTeams([]);
      setDraftTeams([]);
      setViewBy((prev) => (prev === 'team' ? 'department' : prev));
    }
  }, [teamsEnabled, isSettingsReady]);

  const projectOptions = useMemo(() => {
    const seen = new Set<string>();
    return data.allBookings
      .reduce<{ value: string; label: string }[]>((acc, b) => {
        if (!seen.has(b.projectId)) {
          seen.add(b.projectId);
          acc.push({ value: b.projectId, label: b.projectName });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data.allBookings]);

  const roleOptions = useMemo(() => {
    const seen = new Set<string>();
    return resources
      .filter((r) => r.id !== 'unallocated' && r.role)
      .reduce<{ value: string; label: string }[]>((acc, r) => {
        const role = r.role!;
        if (!seen.has(role)) { seen.add(role); acc.push({ value: role, label: role }); }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [resources]);

  // ── Sort + paginate resource view rows (one booking per row)
  const resourceTableRows = useMemo(() => {
    // Unallocated-only override
    if (showUnallocatedOnly) return [];

    const rows = [...data.allBookings];
    if (sortCol !== null) {
      rows.sort((a, b) => {
        let va: string | number = '';
        let vb: string | number = '';
        switch (sortCol) {
          case 'resourceName':  va = a.resourceName;  vb = b.resourceName;  break;
          case 'projectName':   va = a.projectName;   vb = b.projectName;   break;
          case 'bookingType':   va = a.bookingType;   vb = b.bookingType;   break;
          case 'startDate':     va = a.startDate.getTime(); vb = b.startDate.getTime(); break;
          case 'endDate':       va = a.endDate.getTime();   vb = b.endDate.getTime();   break;
          case 'allocationPct': va = a.allocationPct; vb = b.allocationPct; break;
          case 'hours':         va = a.hours;         vb = b.hours;         break;
          default:              va = a.resourceName;  vb = b.resourceName;
        }
        if (typeof va === 'string') {
          return sortDir === 'asc' ? va.localeCompare(vb as string) : (vb as string).localeCompare(va);
        }
        return sortDir === 'asc' ? (va as number) - (vb as number) : (vb as number) - (va as number);
      });
    }
    return rows;
  }, [data.allBookings, sortCol, sortDir, showUnallocatedOnly]);

  // ── Grouped view rows (team / department / role / location).
  // Pagination is applied to the groups themselves, after grouping.
  const isGroupedView = viewBy !== 'individual';

  const groupedTableRows = useMemo(() => {
    if (showUnallocatedOnly || !isGroupedView) return [];
    return data.groupedViews[viewBy] ?? [];
  }, [data.groupedViews, viewBy, isGroupedView, showUnallocatedOnly]);

  const totalRowCount = isGroupedView ? groupedTableRows.length : resourceTableRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRowCount / pageSize));
  // Nothing to page through until there are more rows than the smallest page size.
  const paginationDisabled = totalRowCount <= MIN_PAGE_SIZE;
  const paginatedRows = resourceTableRows.slice((page - 1) * pageSize, page * pageSize);
  const paginatedGroups = groupedTableRows.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handleSort = useCallback(
    (col: string) => {
      setSortCol((prev) => {
        if (prev === col) {
          if (sortDir === 'asc') {
            setSortDir('desc');
            return col;
          } else {
            // third click → reset (no sort)
            setSortDir('asc');
            return null;
          }
        } else {
          setSortDir('asc');
          return col;
        }
      });
      setPage(1);
    },
    [sortDir],
  );

  // ── Group bookings by resource for resource-view table (grouped rows)
  const resourceViewGroups = useMemo(() => {
    if (showUnallocatedOnly) return [];
    return data.resourceGroups;
  }, [data.resourceGroups, showUnallocatedOnly]);

  // ── Filter drawer handlers
  const openFilterDrawer = useCallback(() => {
    setDraftResources(selectedResources);
    setDraftDepartments(selectedDepartments);
    setDraftTeams(selectedTeams);
    setDraftProjects(selectedProjects);
    setDraftRoles(selectedRoles);
    setFilterDrawerOpen(true);
  }, [selectedResources, selectedDepartments, selectedTeams, selectedProjects, selectedRoles]);

  const handleApplyFilters = useCallback(() => {
    setSelectedResources(draftResources);
    setSelectedDepartments(draftDepartments);
    setSelectedTeams(draftTeams);
    setSelectedProjects(draftProjects);
    setSelectedRoles(draftRoles);
    setPage(1);
    setFilterDrawerOpen(false);
  }, [draftResources, draftDepartments, draftTeams, draftProjects, draftRoles]);

  const handleClearDraftFilters = useCallback(() => {
    setDraftResources([]);
    setDraftDepartments([]);
    setDraftTeams([]);
    setDraftProjects([]);
    setDraftRoles([]);
    setSelectedResources([]);
    setSelectedDepartments([]);
    setSelectedTeams([]);
    setSelectedProjects([]);
    setSelectedRoles([]);
    setPage(1);
    setFilterDrawerOpen(false);
  }, []);

  // ── Reset
  const handleReset = useCallback(() => {
    const { from, to } = computeDateRange(defaultPeriod);
    setDateFrom(from);
    setDateTo(to);
    setPeriod(defaultPeriod);
    setViewBy('individual');
    setBookingTypeFilter('both');
    setSelectedResources([]);
    setSelectedDepartments([]);
    setSelectedTeams([]);
    setSelectedProjects([]);
    setSelectedRoles([]);
    setConflictsOnly(false);
    setShowUnallocatedOnly(false);
    setSortCol('resourceName');
    setSortDir('asc');
    setPage(1);
  }, []);

  // ── Export to Excel
  const handleExportExcel = useCallback(() => {
    if (data.allBookings.length === 0 && data.unallocatedResources.length === 0) {
      toast.error('Nothing to export', { description: 'No data matches the current filters.' });
      return;
    }

    const dateLabel = `${format(dateFrom, 'dd MMM yyyy')} – ${format(dateTo, 'dd MMM yyyy')}`;

    const wb = XLSX.utils.book_new();

    if (viewMode === 'resource') {
      // Sheet 1 — bookings by resource
      const bookingRows = data.allBookings.map((b) => ({
        'Resource':      b.resourceName,
        'Role':          b.resourceRole,
        'Department':    b.resourceDept,
        'Project':       b.projectName,
        'Booking type':  b.bookingType.charAt(0).toUpperCase() + b.bookingType.slice(1),
        'Start date':    b.startDateStr,
        'End date':      b.endDateStr,
        'Allocation %':  b.allocationPct,
        'Hours':         b.hours,
        'Conflict':      b.hasConflict ? 'Yes' : 'No',
        'Period':        dateLabel,
      }));

      if (bookingRows.length > 0) {
        const ws = XLSX.utils.json_to_sheet(bookingRows);
        ws['!cols'] = Object.keys(bookingRows[0]).map((k) => ({
          wch: Math.max(k.length, ...bookingRows.map((r) => String(r[k as keyof typeof r]).length)) + 2,
        }));
        XLSX.utils.book_append_sheet(wb, ws, 'Resource Allocation');
      }

      // Sheet 2 — unallocated
      if (data.unallocatedResources.length > 0) {
        const unallocRows = data.unallocatedResources.map((r) => ({
          'Resource':   r.name,
          'Role':       r.role ?? '',
          'Department': r.department ?? '',
          'Status':     'Unallocated',
          'Period':     dateLabel,
        }));
        const ws2 = XLSX.utils.json_to_sheet(unallocRows);
        ws2['!cols'] = Object.keys(unallocRows[0]).map((k) => ({
          wch: Math.max(k.length, ...unallocRows.map((r) => String(r[k as keyof typeof r]).length)) + 2,
        }));
        XLSX.utils.book_append_sheet(wb, ws2, 'Unallocated Resources');
      }
    } else {
      // Project view export
      const projectRows = data.allBookings.map((b) => ({
        'Project':        b.projectName,
        'Client':         b.clientName,
        'Resource':       b.resourceName,
        'Role':           b.resourceRole,
        'Allocation %':   b.allocationPct,
        'Hours':          Math.round(b.hours),
        'Start date':     b.startDateStr,
        'End date':       b.endDateStr,
        'Booking type':   b.bookingType.charAt(0).toUpperCase() + b.bookingType.slice(1),
        'Period':         dateLabel,
      }));

      if (projectRows.length > 0) {
        const ws = XLSX.utils.json_to_sheet(projectRows);
        ws['!cols'] = Object.keys(projectRows[0]).map((k) => ({
          wch: Math.max(k.length, ...projectRows.map((r) => String(r[k as keyof typeof r]).length)) + 2,
        }));
        XLSX.utils.book_append_sheet(wb, ws, 'Project Staffing');
      }
    }

    const filename = `resource-allocation-${format(dateFrom, 'yyyy-MM-dd')}-to-${format(dateTo, 'yyyy-MM-dd')}.xlsx`;
    XLSX.writeFile(wb, filename);

    toast.success('Export complete', {
      description: `${data.allBookings.length} booking${data.allBookings.length !== 1 ? 's' : ''} exported.`,
    });
  }, [data, dateFrom, dateTo, viewMode]);

  // ── Active filter count (for the filter indicator)
  const activeFilterCount = [
    selectedResources.length > 0,
    selectedDepartments.length > 0,
    selectedTeams.length > 0,
    selectedProjects.length > 0,
    selectedRoles.length > 0,
    conflictsOnly,
    showUnallocatedOnly,
    bookingTypeFilter !== 'both',
    viewBy !== 'individual',
  ].filter(Boolean).length;

  const { ref: contentRef, gutter: contentGutter } = useScrollbarGutter<HTMLDivElement>();
  const gutterPadding = { paddingRight: `calc(1.5rem + ${contentGutter}px)` };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">

      {/* ── Page header ──────────────────────────────────────────────── */}
      <div
        className="flex items-center justify-between px-6 py-4 shrink-0"
        style={gutterPadding}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
            <BarChart2 className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground leading-tight">
              Resource allocation
            </h1>
            <p className="text-[13px] text-muted-foreground mt-0.5">
              All bookings per resource — confirmed and planned
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-4">
          <FeatureFeedbackCollector feature="Resource Allocation" />
          <HelpButton />
        </div>
      </div>

      {/* ── Filters bar ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap px-6 py-3 shrink-0" style={gutterPadding}>
        {/* Period */}
        <Select value={period} onValueChange={(v) => handlePeriodChange(v as PeriodKey)}>
          <SelectTrigger className="h-8 w-auto min-w-[130px] shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="week">This week</SelectItem>
            <SelectItem value="month">This month</SelectItem>
            <SelectItem value="quarter">This quarter</SelectItem>
            <SelectItem value="rolling12">Rolling 12 months</SelectItem>
          </SelectContent>
        </Select>

        {/* Prev / Next arrows + custom date inputs */}
        <div className="inline-flex items-center gap-1 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 shrink-0"
            onClick={() => handleStepPeriod(-1)}
            aria-label="Previous period"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="inline-flex items-center gap-1.5">
            <Input
              type="date"
              value={format(dateFrom, 'yyyy-MM-dd')}
              onChange={handleCustomDateFrom}
              className="h-8 px-2 text-sm border-input"
              aria-label="Start date"
            />
            <span className="text-sm text-muted-foreground">—</span>
            <Input
              type="date"
              value={format(dateTo, 'yyyy-MM-dd')}
              readOnly
              disabled
              className="h-8 px-2 text-sm border-input"
              aria-label="End date"
              title="End date is calculated from the start date and the selected period"
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 shrink-0"
            onClick={() => handleStepPeriod(1)}
            aria-label="Next period"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* View by (Resource view only) */}
        {viewMode === 'resource' && (
          <Select value={viewBy} onValueChange={(v) => { setViewBy(v as ViewBy); setPage(1); }}>
            <SelectTrigger className="h-8 w-auto min-w-[150px] shrink-0">
              <SelectValue placeholder="View by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="individual">Individual</SelectItem>
              {groupingFilter === 'team' && <SelectItem value="team">Planner Group</SelectItem>}
              {groupingFilter === 'department' && <SelectItem value="department">Department</SelectItem>}
              <SelectItem value="role">Role</SelectItem>
              <SelectItem value="location">Location</SelectItem>
            </SelectContent>
          </Select>
        )}

        {/* Booking type segmented */}
        <Segmented
          options={[
            { label: 'Both',      value: 'both' },
            { label: 'Confirmed', value: 'confirmed' },
            { label: 'Planned',   value: 'planned' },
          ]}
          value={bookingTypeFilter}
          onChange={(v) => { setBookingTypeFilter(v as BookingTypeFilter); setPage(1); }}
        />

        <div className="ml-auto flex items-center gap-2">
          {/* Conflicts only toggle — kept inline as it's a primary content filter */}
          <Button
            type="button"
            variant={conflictsOnly ? 'default' : 'outline'}
            size="sm"
            className="h-8 gap-1.5 shrink-0"
            onClick={() => {
              setConflictsOnly((prev) => {
                const next = !prev;
                if (next) setShowUnallocatedOnly(false);
                return next;
              });
              setPage(1);
            }}
            aria-pressed={conflictsOnly}
          >
            <AlertCircle className="h-4 w-4" />
            Conflicts only
          </Button>

          {/* Reset — only when secondary filters are active */}
          {(selectedResources.length > 0 || selectedDepartments.length > 0 || selectedTeams.length > 0 || selectedProjects.length > 0) && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1"
              onClick={handleReset}
            >
              <X className="h-4 w-4" />
              Reset
            </Button>
          )}

          {/* Filter button */}
          <Button
            variant={
              selectedResources.length > 0 ||
              selectedDepartments.length > 0 ||
              selectedTeams.length > 0 ||
              selectedProjects.length > 0
                ? 'default'
                : 'outline'
            }
            size="sm"
            className="relative h-8 w-8 p-0 shrink-0"
            onClick={openFilterDrawer}
            aria-label="Open filters"
          >
            <Filter className="h-4 w-4" />
            {(() => {
              const count =
                (selectedResources.length > 0 ? 1 : 0) +
                (selectedDepartments.length > 0 ? 1 : 0) +
                (selectedTeams.length > 0 ? 1 : 0) +
                (selectedProjects.length > 0 ? 1 : 0);
              return count > 0 ? (
                <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground leading-none">
                  {count}
                </span>
              ) : null;
            })()}
          </Button>

          {/* Column config button */}
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 shrink-0"
            onClick={() => setColConfigOpen(true)}
            aria-label="Configure columns"
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>

          {/* Export to Excel */}
          {canExport && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0 shrink-0"
                  onClick={handleExportExcel}
                  aria-label="Export resource allocation report to Excel"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Export to Excel</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          )}
        </div>
      </div>

      {/* ── Filter drawer ─────────────────────────────────────────────────── */}
      {/* ── Column config sheet ─────────────────────────────────────────── */}
      <Sheet open={colConfigOpen} onOpenChange={setColConfigOpen}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Configure columns</SheetTitle>
          <SheetDescription className="sr-only">Toggle columns on or off for the resource allocation table.</SheetDescription>
          <SheetHeader className="px-6 pt-6 pb-5 border-b shrink-0">
            <div className="flex items-center gap-3 pr-8">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold leading-tight">Customise Columns</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">Toggle columns on or off to personalise the table.</p>
              </div>
            </div>
          </SheetHeader>
          <div className="px-4 py-4 flex-1">
            <p className="text-sm text-muted-foreground">Column configuration is available for this report. Use the individual column headers to sort and interact with your data.</p>
          </div>
          <SheetFooter className="px-4 py-3 border-t shrink-0">
            <Button className="w-full" onClick={() => setColConfigOpen(false)}>Done</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <SheetContent side="right" className="w-[380px] sm:max-w-[380px] flex flex-col p-0 gap-0">
          <SheetTitle className="sr-only">Resource allocation filters</SheetTitle>
          <SheetDescription className="sr-only">Filter by resource, department, planner group and project.</SheetDescription>
          {/* Header */}
          <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0 gap-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">Filters</span>
              {(() => {
                const count = (draftResources.length > 0 ? 1 : 0) + (draftDepartments.length > 0 ? 1 : 0) + (draftTeams.length > 0 ? 1 : 0) + (draftProjects.length > 0 ? 1 : 0) + (draftRoles.length > 0 ? 1 : 0) + (showUnallocatedOnly ? 1 : 0);
                return (
                  <Badge variant="secondary" className="flex items-center gap-1 px-2.5 py-0.5 h-6 text-[11px] font-medium rounded-full bg-primary/10 text-primary border-0 hover:bg-primary/10">
                    <Filter className="h-3 w-3" />
                    {count} {count === 1 ? 'filter' : 'filters'} active
                  </Badge>
                );
              })()}
            </div>
          </SheetHeader>
          {/* Body */}
          <ScrollArea className="flex-1">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Resource</label>
                  <MultiSelect options={resourceOptions} placeholder="All resources" value={draftResources} onValueChange={setDraftResources} maxDisplay={2} />
                </div>
                <Separator />
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Role</label>
                  <MultiSelect options={roleOptions} placeholder="All roles" value={draftRoles} onValueChange={setDraftRoles} maxDisplay={2} />
                </div>
                <Separator />
                {groupingFilter === 'team' ? (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-foreground">Planner Group</label>
                    <MultiSelect options={teamOptions} placeholder="All planner groups" value={draftTeams} onValueChange={setDraftTeams} maxDisplay={2} />
                  </div>
                ) : groupingFilter === 'department' ? (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-foreground">Department</label>
                    <MultiSelect options={departmentOptions} placeholder="All departments" value={draftDepartments} onValueChange={setDraftDepartments} maxDisplay={2} />
                  </div>
                ) : null}
                <Separator />
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Project</label>
                  <MultiSelect options={projectOptions} placeholder="All projects" value={draftProjects} onValueChange={setDraftProjects} maxDisplay={2} />
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-foreground">Show unallocated only</label>
                  <Switch
                    checked={showUnallocatedOnly}
                    onCheckedChange={(checked) => {
                      setShowUnallocatedOnly(checked);
                      if (checked) setConflictsOnly(false);
                      setPage(1);
                    }}
                    aria-label="Show unallocated resources only"
                  />
                </div>
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
          {/* Footer */}
          <SheetFooter className="px-4 py-3 border-t shrink-0 flex-row items-center justify-between gap-2">
            <Button
              variant="ghost"
              className="h-9 gap-1.5 text-muted-foreground hover:text-foreground px-2"
              onClick={handleClearDraftFilters}
              disabled={(draftResources.length + draftDepartments.length + draftTeams.length + draftProjects.length + draftRoles.length) === 0 && !showUnallocatedOnly}
            >
              <X className="h-3.5 w-3.5" />
              Clear All
            </Button>
            <Button className="h-9 gap-1.5 px-5" onClick={handleApplyFilters}>
              <Check className="h-3.5 w-3.5" />
              Apply Filters
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Scrollable content ────────────────────────────────────────── */}
      <div ref={contentRef} className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">

        {isLiveDataLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading allocation data…</p>
          </div>
        ) : liveBookingsError ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <p className="text-sm font-medium text-foreground">Failed to load booking data</p>
            <p className="text-xs text-muted-foreground">{liveBookingsError}</p>
            <Button variant="outline" size="sm" onClick={() => fetchLiveBookings(dateFrom, dateTo)}>Retry</Button>
          </div>
        ) : data.isEmpty && !showUnallocatedOnly ? (
          <EmptyState onReset={handleReset} />
        ) : (
          <>
            {/* ── Stat cards ─────────────────────────────────────────── */}
            <div className="flex gap-3">
              {/* Total bookings */}
              <StatCard
                value={data.summary.totalBookings}
                label="Total bookings in period"
                subLabel="Confirmed and planned bookings"
              />

              {/* Resources with conflicts — red if any, clickable to filter */}
              <StatCard
                value={data.summary.resourcesWithConflicts}
                label="Resources with conflicts"
                subLabel="Overlapping bookings in the period"
                valueColour={data.summary.resourcesWithConflicts > 0 ? 'destructive' : 'default'}
                isFilterable={data.summary.resourcesWithConflicts > 0}
                isActive={conflictsOnly}
                onClick={() => {
                  setConflictsOnly((prev) => !prev);
                  if (!conflictsOnly) setShowUnallocatedOnly(false);
                  setPage(1);
                }}
              />

              {/* Average allocation % */}
              <StatCard
                value={`${data.summary.avgAllocationPct}%`}
                label="Average allocation"
                subLabel="Across all booked resources in period"
                valueColour={
                  data.summary.avgAllocationPct > 100 ? 'destructive'
                  : data.summary.avgAllocationPct >= 75 ? 'teal'
                  : 'default'
                }
              />

              {/* Resources booked */}
              <StatCard
                value={data.summary.resourcesBooked}
                label="Resources booked"
                subLabel="Distinct resources with bookings in period"
                valueColour="teal"
              />
            </div>

            {/* ── Active filter pills ────────────────────────────────── */}
            {(conflictsOnly || showUnallocatedOnly) && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[12px] text-muted-foreground">Filtered by:</span>
                {conflictsOnly && (
                  <button
                    type="button"
                    onClick={() => { setConflictsOnly(false); setPage(1); }}
                    className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[11px] font-medium bg-destructive/10 text-destructive border border-destructive/20 hover:bg-destructive/20 transition-colors"
                  >
                    Conflicts only
                    <X className="h-3 w-3" />
                  </button>
                )}
                {showUnallocatedOnly && (
                  <button
                    type="button"
                    onClick={() => { setShowUnallocatedOnly(false); setPage(1); }}
                    className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[11px] font-medium bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                  >
                    Unallocated only
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}

            {/* ── View mode toggle ─────────────────────────────────────── */}
            {!showUnallocatedOnly && (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <Segmented
                  options={[
                    { label: 'Resource view', value: 'resource' },
                    { label: 'Project view',  value: 'project' },
                  ]}
                  value={viewMode}
                  onChange={(v) => { setViewMode(v as ViewMode); setPage(1); setSortCol('resourceName'); setSortDir('asc'); }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 text-[12px] text-muted-foreground hover:text-foreground shrink-0"
                  onClick={() => onNavigate?.('heatmap')}
                  aria-label="View resource heatmap"
                >
                  View in heatmap
                  <ExternalLink className="h-3 w-3" />
                </Button>
              </div>
            )}

            {/* ── Table: Grouped view (team / department / role / location) ── */}
            {viewMode === 'resource' && !showUnallocatedOnly && viewBy !== 'individual' && (() => {
              const groupLabel = viewBy === 'team' ? 'Planner Group' : viewBy === 'department' ? 'Department' : viewBy === 'role' ? 'Role' : 'Location';
              const groups = paginatedGroups;
              return (
                <div
                  className={RAW_TABLE_CONTAINER_CLASS}
                >
                  <div className={RAW_TABLE_SCROLL_CLASS}>
                    <table className={RAW_TABLE_CLASS}>
                      <thead>
                        <tr className={TABLE_HEADER_ROW_CLASS}>
                          <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left" style={{ minWidth: 200 }}>{groupLabel}</th>
                          <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left">Resources</th>
                          <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left">Bookings</th>
                          <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-right">Avg allocation</th>
                          <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left">Conflicts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groups.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-3 py-8 text-center text-[13px] text-muted-foreground">
                              No data matches the current filters.
                            </td>
                          </tr>
                        ) : groups.map((g, gIdx) => (
                          <React.Fragment key={g.groupKey}>
                            {/* Group header row */}
                            <tr
                              style={{
                                background: 'var(--color-background-secondary, var(--muted))',
                                borderTop: gIdx > 0 ? '0.5px solid var(--border)' : undefined,
                              }}
                            >
                              <td colSpan={5} className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-[13px] font-semibold text-foreground">{g.groupLabel}</span>
                                  <span className="text-[11px] text-muted-foreground">{g.resources.length} resource{g.resources.length !== 1 ? 's' : ''}</span>
                                  {g.hasConflict && (
                                    <span className="inline-flex items-center gap-1 text-[11px] text-destructive font-medium">
                                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-destructive" />
                                      Conflicts
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {/* Resource rows within this group */}
                            {g.resources.map((rg, rgIdx) => {
                              const isEven = rgIdx % 2 === 0;
                              return (
                                <tr
                                  key={rg.resourceId}
                                  className="cursor-pointer transition-colors"
                                  style={{ height: 44, background: isEven ? 'var(--background)' : TABLE_STRIPE_BG }}
                                  onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = TABLE_HOVER_BG; }}
                                  onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = isEven ? 'var(--background)' : TABLE_STRIPE_BG; }}
                                  onClick={() => setDrawerGroup(rg)}
                                >
                                  <td className="pl-8 pr-3 py-2" style={{ minWidth: 200 }}>
                                    <div className="flex items-center gap-2 min-w-0">
                                      <Avatar className="h-6 w-6 shrink-0">
                                        {rg.resourcePhotoUrl && <img src={rg.resourcePhotoUrl} alt={rg.resourceName} className="h-6 w-6 rounded-full object-cover" />}
                                        <AvatarFallback className="text-[9px] font-semibold bg-primary/10 text-primary">{rg.resourceInitials}</AvatarFallback>
                                      </Avatar>
                                      <div className="min-w-0">
                                        <p className="text-[13px] font-medium text-foreground truncate">{rg.resourceName}</p>
                                        <p className="text-[11px] text-muted-foreground truncate">{rg.resourceRole}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-[13px] text-muted-foreground">
                                    {viewBy !== 'role' ? rg.resourceRole || '—' : rg.resourceDept || '—'}
                                  </td>
                                  <td className="px-3 py-2 text-[13px] text-foreground">{rg.bookings.length}</td>
                                  <td className="px-3 py-2 text-right">
                                    <AllocationCell pct={rg.totalAllocationPct} />
                                  </td>
                                  <td className="px-3 py-2">
                                    <ConflictFlag
                                      hasConflict={rg.hasConflict}
                                      onClick={() => handleResolveConflict(rg.resourceId)}
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            {/* ── Table: Resource view (individual) ────────────────────── */}
            {viewMode === 'resource' && !showUnallocatedOnly && viewBy === 'individual' && (
              <div
                className={RAW_TABLE_CONTAINER_CLASS}
              >
                <div className={RAW_TABLE_SCROLL_CLASS}>
                  <table className={RAW_TABLE_CLASS}>
                    <thead>
                      <tr className={TABLE_HEADER_ROW_CLASS}>
                        <SortHeader label="Resource"      col="resourceName"  sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={200} />
                        <SortHeader label="Project"       col="projectName"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} minWidth={160} />
                        <SortHeader label="Booking type"  col="bookingType"   sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label="Start"         col="startDate"     sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label="End"           col="endDate"       sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label="Allocation %"  col="allocationPct" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                        <SortHeader label="Hours"         col="hours"         sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                        <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left whitespace-nowrap">
                          Conflict
                        </th>
                        <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left whitespace-nowrap">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Group by resource with subtle group header row */}
                      {(() => {
                        // Build grouped display: header row per resource, then booking rows
                        const groupedDisplay: React.ReactNode[] = [];
                        let rowIdx = 0;

                        // Determine which resources are visible in the current page
                        // We paginate booking rows but group them
                        const pageBookings = paginatedRows;
                        const seenResourceIds = new Set<string>();

                        for (const b of pageBookings) {
                          const rg = data.resourceGroups.find(
                            (g) => g.resourceId === b.resourceId,
                          );

                          // Group header — emitted once per resource in this page
                          if (!seenResourceIds.has(b.resourceId)) {
                            seenResourceIds.add(b.resourceId);
                            groupedDisplay.push(
                              <tr
                                key={`header-${b.resourceId}`}
                                className="cursor-pointer"
                                onClick={() => rg && setDrawerGroup(rg)}
                                style={{
                                  background: 'var(--color-background-secondary, var(--muted))',
                                  borderTop: rowIdx > 0 ? '0.5px solid var(--border)' : undefined,
                                }}
                              >
                                <td colSpan={9} className="px-3 py-2">
                                  <div className="flex items-center gap-2.5">
                                    <Avatar className="h-7 w-7 shrink-0">
                                      {b.resourcePhotoUrl && (
                                        <img src={b.resourcePhotoUrl} alt={b.resourceName} className="h-7 w-7 rounded-full object-cover" />
                                      )}
                                      <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                                        {b.resourceInitials}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span className="text-[13px] font-semibold text-foreground">
                                      {b.resourceName}
                                    </span>
                                    <span className="text-[11px] text-muted-foreground">
                                      {b.resourceRole}
                                    </span>
                                    {rg && (
                                      <span className="text-[11px] text-muted-foreground ml-auto">
                                        {rg.totalAllocationPct}% total allocation
                                      </span>
                                    )}
                                    {rg?.hasConflict && (
                                      <span className="inline-flex items-center gap-1 text-[11px] text-destructive font-medium">
                                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-destructive" />
                                        Conflict
                                      </span>
                                    )}
                                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground ml-1" />
                                  </div>
                                </td>
                              </tr>,
                            );
                            rowIdx++;
                          }

                          // Booking row
                          const isEven = rowIdx % 2 === 0;
                          groupedDisplay.push(
                            <tr
                              key={b.id}
                              className="cursor-pointer transition-colors"
                              style={{
                                height: 44,
                                background: isEven ? 'var(--background)' : TABLE_STRIPE_BG,
                              }}
                              onMouseEnter={(e) => {
                                (e.currentTarget as HTMLTableRowElement).style.background = TABLE_HOVER_BG;
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLTableRowElement).style.background =
                                  isEven ? 'var(--background)' : TABLE_STRIPE_BG;
                              }}
                              onClick={() => rg && setDrawerGroup(rg)}
                            >
                              {/* Resource (indented) */}
                              <td className="pl-8 pr-3 py-2" style={{ minWidth: 200 }}>
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="w-1.5 h-1.5 rounded-full bg-border shrink-0" />
                                  <p className="text-[13px] text-muted-foreground truncate">
                                    {b.resourceName}
                                  </p>
                                </div>
                              </td>
                              {/* Project */}
                              <td className="px-3 py-2" style={{ minWidth: 160 }}>
                                <p className="text-[13px] font-medium text-foreground truncate">
                                  {b.projectName}
                                </p>
                              </td>
                              {/* Booking type */}
                              <td className="px-3 py-2 whitespace-nowrap">
                                <BookingTypePill type={b.bookingType} />
                              </td>
                              {/* Start */}
                              <td className="px-3 py-2 text-foreground whitespace-nowrap text-[13px]">
                                {b.startDateStr}
                              </td>
                              {/* End */}
                              <td className="px-3 py-2 text-foreground whitespace-nowrap text-[13px]">
                                {b.endDateStr}
                              </td>
                              {/* Allocation % — red only on this booking row when it has a conflict */}
                              <td className="px-3 py-2 text-right">
                                <AllocationCell pct={b.allocationPct} forceRed={b.hasConflict} />
                              </td>
                              {/* Hours */}
                              <td className="px-3 py-2 text-right text-foreground text-[13px]">
                                {b.hours}h
                              </td>
                              {/* Conflict — show dot+label only on this booking row if it overlaps */}
                              <td className="px-3 py-2">
                                {b.hasConflict ? (
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="inline-flex items-center gap-1 text-destructive text-[11px] font-medium whitespace-nowrap">
                                      <span className="inline-block h-2 w-2 rounded-full bg-destructive shrink-0" />
                                      Conflict
                                    </span>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleResolveConflict(b.resourceId); }}
                                      className="text-[11px] font-medium whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                      style={{ color: 'var(--teal-600, oklch(0.45 0.08 194))' }}
                                    >
                                      Resolve ›
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-[12px]">—</span>
                                )}
                              </td>
                              {/* Status */}
                              <td className="px-3 py-2">
                                <BookingTypePill type={b.status} />
                              </td>
                            </tr>,
                          );
                          rowIdx++;
                        }

                        if (groupedDisplay.length === 0) {
                          return (
                            <tr>
                              <td colSpan={9} className="px-3 py-8 text-center text-[13px] text-muted-foreground">
                                No bookings match the current filters.
                              </td>
                            </tr>
                          );
                        }

                        return groupedDisplay;
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Table: Project view ──────────────────────────────────── */}
            {viewMode === 'project' && !showUnallocatedOnly && (
              <div
                className={RAW_TABLE_CONTAINER_CLASS}
              >
                <div className={RAW_TABLE_SCROLL_CLASS}>
                  <table className={RAW_TABLE_CLASS}>
                    <thead>
                      <tr className={TABLE_HEADER_ROW_CLASS}>
                        <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left" style={{ minWidth: 200 }}>
                          Resource / Project
                        </th>
                        <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left whitespace-nowrap" style={{ minWidth: 160 }}>
                          Role
                        </th>
                        <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left whitespace-nowrap">
                          Booking type
                        </th>
                        <SortHeader label="Start"        col="startDate"     sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label="End"          col="endDate"       sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                        <SortHeader label="Allocation %" col="allocationPct" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                        <SortHeader label="Hours"        col="hours"         sortCol={sortCol} sortDir={sortDir} onSort={handleSort} align="right" />
                        <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left whitespace-nowrap">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.projectGroups.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-3 py-8 text-center text-[13px] text-muted-foreground">
                            No projects match the current filters.
                          </td>
                        </tr>
                      ) : (
                        data.projectGroups.map((pg, pgIdx) => {
                          // ── Consolidate bookings by resource for this project ──────────────
                          const consolidatedByResource = new Map<string, {
                            resourceId: string;
                            resourceName: string;
                            resourceRole: string;
                            resourceInitials: string;
                            resourcePhotoUrl?: string;
                            combinedAllocationPct: number;
                            earliestStart: Date;
                            latestEnd: Date;
                            earliestStartStr: string;
                            latestEndStr: string;
                            bookingType: 'confirmed' | 'planned' | 'mixed';
                            bookings: typeof pg.bookings;
                          }>();

                          for (const b of pg.bookings) {
                            if (!consolidatedByResource.has(b.resourceId)) {
                              consolidatedByResource.set(b.resourceId, {
                                resourceId:           b.resourceId,
                                resourceName:         b.resourceName,
                                resourceRole:         b.resourceRole,
                                resourceInitials:     b.resourceInitials,
                                resourcePhotoUrl:     b.resourcePhotoUrl,
                                combinedAllocationPct: 0,
                                earliestStart:        b.startDate,
                                latestEnd:            b.endDate,
                                earliestStartStr:     b.startDateStr,
                                latestEndStr:         b.endDateStr,
                                bookingType:          b.bookingType,
                                bookings:             [],
                              });
                            }
                            const c = consolidatedByResource.get(b.resourceId)!;
                            c.bookings.push(b);
                            c.combinedAllocationPct += b.allocationPct;
                            if (b.startDate < c.earliestStart) {
                              c.earliestStart    = b.startDate;
                              c.earliestStartStr = b.startDateStr;
                            }
                            if (b.endDate > c.latestEnd) {
                              c.latestEnd    = b.endDate;
                              c.latestEndStr = b.endDateStr;
                            }
                            // Compute mixed type
                            const hasConf = c.bookings.some((x) => x.bookingType === 'confirmed');
                            const hasPlan = c.bookings.some((x) => x.bookingType === 'planned');
                            c.bookingType = hasConf && hasPlan ? 'mixed' : hasConf ? 'confirmed' : 'planned';
                          }

                          const consolidatedRows = Array.from(consolidatedByResource.values());

                          return (
                          <React.Fragment key={pg.projectId}>
                            {/* Project group header */}
                            <tr
                              style={{
                                background: 'var(--color-background-secondary, var(--muted))',
                                borderTop: pgIdx > 0 ? '0.5px solid var(--border)' : undefined,
                              }}
                            >
                              <td colSpan={8} className="px-3 py-2">
                                <div className="flex items-center gap-3 flex-wrap">
                                  <span className="text-[13px] font-semibold text-foreground">
                                    {pg.projectName}
                                  </span>
                                  {pg.clientName && pg.clientName !== '—' && (
                                    <span className="text-[11px] text-muted-foreground">
                                      {pg.clientName}
                                    </span>
                                  )}
                                  <span className="text-[11px] text-muted-foreground">
                                    {pg.totalResources} resource{pg.totalResources !== 1 ? 's' : ''}
                                  </span>
                                  <span className="text-[11px] text-muted-foreground">
                                    {pg.bookings.length} booking{pg.bookings.length !== 1 ? 's' : ''}
                                  </span>
                                  <span className="text-[11px] text-muted-foreground">
                                    {Math.round(pg.bookings.reduce((s, b) => s + b.hours, 0))}h total
                                  </span>
                                  {pg.startDateStr && (
                                    <span className="text-[11px] text-muted-foreground">
                                      {pg.startDateStr} – {pg.endDateStr}
                                    </span>
                                  )}
                                  <span className="ml-auto">
                                    <StaffingBadge status={pg.staffingStatus} unfilledRoleCount={pg.unfilledRoleCount} />
                                  </span>
                                </div>
                              </td>
                            </tr>

                            {/* Consolidated resource rows under this project */}
                            {consolidatedRows.map((c, cIdx) => {
                              const isEven = cIdx % 2 === 0;
                              const rg = data.resourceGroups.find(
                                (g) => g.resourceId === c.resourceId,
                              );
                              const rowKey = `${pg.projectId}-${c.resourceId}`;
                              const isExpanded = expandedConsolidated.has(rowKey);
                              const isMultiBooking = c.bookings.length > 1;

                              return (
                                <React.Fragment key={rowKey}>
                                  {/* Consolidated summary row */}
                                  <tr
                                    className="cursor-pointer transition-colors"
                                    style={{
                                      height: 44,
                                      background: isEven ? 'var(--background)' : TABLE_STRIPE_BG,
                                    }}
                                    onMouseEnter={(e) => {
                                      (e.currentTarget as HTMLTableRowElement).style.background = TABLE_HOVER_BG;
                                    }}
                                    onMouseLeave={(e) => {
                                      (e.currentTarget as HTMLTableRowElement).style.background =
                                        isEven ? 'var(--background)' : TABLE_STRIPE_BG;
                                    }}
                                    onClick={() => toggleConsolidatedRow(rowKey)}
                                  >
                                  {/* Resource */}
                                  <td className="pl-8 pr-3 py-2" style={{ minWidth: 200 }}>
                                    <div className="flex items-center gap-2.5 min-w-0">
                                      <span className="text-muted-foreground shrink-0">
                                        {isExpanded
                                          ? <ChevronDown className="h-3.5 w-3.5" />
                                          : <ChevronRight className="h-3.5 w-3.5" />}
                                      </span>
                                      <Avatar className="h-6 w-6 shrink-0">
                                        {c.resourcePhotoUrl && (
                                          <img src={c.resourcePhotoUrl} alt={c.resourceName} className="h-6 w-6 rounded-full object-cover" />
                                        )}
                                        <AvatarFallback className="text-[9px] font-semibold bg-primary/10 text-primary">
                                          {c.resourceInitials}
                                        </AvatarFallback>
                                      </Avatar>
                                      <div className="min-w-0">
                                        <p className="text-[13px] font-medium text-foreground truncate">
                                          {c.resourceName}
                                        </p>
                                        <p className="text-[10px] text-muted-foreground">
                                          {c.bookings.length} booking{c.bookings.length !== 1 ? 's' : ''}
                                        </p>
                                      </div>
                                    </div>
                                  </td>
                                  {/* Role */}
                                  <td className="px-3 py-2 text-muted-foreground text-[13px] whitespace-nowrap" style={{ minWidth: 160 }}>
                                    {c.resourceRole}
                                  </td>
                                  {/* Booking type — Mixed pill when both exist */}
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    {c.bookingType === 'mixed'
                                      ? <MixedPill />
                                      : <BookingTypePill type={c.bookingType as 'confirmed' | 'planned'} />
                                    }
                                  </td>
                                  {/* Start — earliest */}
                                  <td className="px-3 py-2 text-foreground whitespace-nowrap text-[13px]">
                                    {c.earliestStartStr}
                                  </td>
                                  {/* End — latest */}
                                  <td className="px-3 py-2 text-foreground whitespace-nowrap text-[13px]">
                                    {c.latestEndStr}
                                  </td>
                                  {/* Allocation % — only on single-booking rows; grouped rows show it per booking */}
                                  <td className="px-3 py-2 text-right">
                                    {isMultiBooking
                                      ? <span className="text-muted-foreground text-[12px]">—</span>
                                      : <AllocationCell pct={c.combinedAllocationPct} />
                                    }
                                  </td>
                                  {/* Hours */}
                                  <td className="px-3 py-2 text-right text-foreground text-[13px]">
                                    {Math.round(c.bookings.reduce((s, b) => s + b.hours, 0))}h
                                  </td>
                                  {/* Status — same logic as booking type */}
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    {c.bookingType === 'mixed'
                                      ? <MixedPill />
                                      : <BookingTypePill type={c.bookingType as 'confirmed' | 'planned'} />
                                    }
                                  </td>
                                  </tr>

                                  {/* Expanded individual booking breakdown */}
                                  {isExpanded && c.bookings.map((b, bSubIdx) => (
                                    <tr
                                      key={`sub-${b.id}`}
                                      className="cursor-pointer transition-colors"
                                      style={{
                                        height: 40,
                                        background: bSubIdx % 2 === 0
                                          ? 'var(--teal-50, #f0fdfa)'
                                          : 'var(--background)',
                                        borderLeft: '3px solid var(--teal-300, oklch(0.7 0.08 194))',
                                      }}
                                      onMouseEnter={(e) => {
                                        (e.currentTarget as HTMLTableRowElement).style.background = 'var(--teal-100, #ccfbf1)';
                                      }}
                                      onMouseLeave={(e) => {
                                        (e.currentTarget as HTMLTableRowElement).style.background = bSubIdx % 2 === 0
                                          ? 'var(--teal-50, #f0fdfa)'
                                          : 'var(--background)';
                                      }}
                                      onClick={() => rg && setDrawerGroup(rg)}
                                    >
                                      <td className="pl-12 pr-3 py-1.5" style={{ minWidth: 200 }}>
                                        <p className="text-[12px] text-muted-foreground truncate">
                                          Booking {bSubIdx + 1}
                                        </p>
                                      </td>
                                      <td className="px-3 py-1.5 text-muted-foreground text-[12px] whitespace-nowrap" style={{ minWidth: 160 }}>
                                        {b.resourceRole}
                                      </td>
                                      <td className="px-3 py-1.5 whitespace-nowrap">
                                        <BookingTypePill type={b.bookingType} />
                                      </td>
                                      <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap text-[12px]">
                                        {b.startDateStr}
                                      </td>
                                      <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap text-[12px]">
                                        {b.endDateStr}
                                      </td>
                                      <td className="px-3 py-1.5 text-right">
                                        <AllocationCell pct={b.allocationPct} />
                                      </td>
                                      <td className="px-3 py-1.5 text-right text-muted-foreground text-[12px]">
                                        {Math.round(b.hours)}h
                                      </td>
                                      <td className="px-3 py-1.5">
                                        <BookingTypePill type={b.status} />
                                      </td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              );
                            })}

                            {/* Unfilled roles — shown as a red gap row */}
                            {pg.unfilledRoles.map((role) => (
                              <tr
                                key={`unfilled-${pg.projectId}-${role}`}
                                style={{ background: '#fff5f5', borderTop: '0.5px solid #fca5a5' }}
                              >
                                <td colSpan={8} className="pl-8 pr-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                                    <span className="text-[12px] text-destructive font-medium">
                                      {role} required · unfilled
                                    </span>
                                    <button
                                      className="inline-flex items-center gap-1 text-[12px] text-primary hover:underline font-medium ml-2"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toast.info(`Opening Add Booking for ${role} on ${pg.projectName}`);
                                      }}
                                    >
                                      Add resource ›
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Unallocated resources table ──────────────────────────── */}
            {showUnallocatedOnly && (
              <div
                className={RAW_TABLE_CONTAINER_CLASS}
              >
                <div className={RAW_TABLE_SCROLL_CLASS}>
                  <table className={RAW_TABLE_CLASS}>
                    <thead>
                      <tr className={TABLE_HEADER_ROW_CLASS}>
                        <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left" style={{ minWidth: 200 }}>
                          Resource
                        </th>
                        <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left">Role</th>
                        <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left">Department</th>
                        <th className="px-3 py-2.5 text-[12px] font-medium text-muted-foreground text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.unallocatedResources.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-3 py-8 text-center text-[13px] text-muted-foreground">
                            No unallocated resources in this period.
                          </td>
                        </tr>
                      ) : (
                        data.unallocatedResources.map((r, idx) => (
                          <tr
                            key={r.id}
                            className="transition-colors"
                            style={{
                              height: 44,
                              background: alternatingRowBackground(idx),
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLTableRowElement).style.background = TABLE_HOVER_BG;
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLTableRowElement).style.background =
                                alternatingRowBackground(idx);
                            }}
                          >
                            <td className="px-3 py-2" style={{ minWidth: 200 }}>
                              <div className="flex items-center gap-2.5">
                                <Avatar className="h-7 w-7 shrink-0">
                                  {r.photoUrl && (
                                    <img src={r.photoUrl} alt={r.name} className="h-7 w-7 rounded-full object-cover" />
                                  )}
                                  <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                                    {getInitials(r.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0">
                                  <p className="font-medium text-[13px] text-foreground truncate leading-tight">
                                    {r.name}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-muted-foreground text-[13px]">{r.role ?? '—'}</td>
                            <td className="px-3 py-2 text-muted-foreground text-[13px]">
                              {r.department ?? r.schedulerGroup ?? '—'}
                            </td>
                            <td className="px-3 py-2">
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                                Unallocated
                              </Badge>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Pagination — resource view only ─────────────────────── */}
            {viewMode === 'resource' && !showUnallocatedOnly && totalRowCount > 0 && (
              <div className="flex items-center justify-between shrink-0 pb-2">
                <p className="text-[13px] text-muted-foreground">
                  Showing {Math.min((page - 1) * pageSize + 1, totalRowCount)}–
                  {Math.min(page * pageSize, totalRowCount)} of {totalRowCount}{' '}
                  {isGroupedView
                    ? `group${totalRowCount !== 1 ? 's' : ''}`
                    : `booking${totalRowCount !== 1 ? 's' : ''}`}
                </p>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={paginationDisabled || page === 1}
                    className="h-8 text-[13px]"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Previous
                  </Button>
                  <span className="text-[13px] text-muted-foreground px-1">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={paginationDisabled || page === totalPages}
                    className="h-8 text-[13px]"
                  >
                    Next
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-[13px] text-muted-foreground">Rows per page:</span>
                  <Select
                    value={String(pageSize)}
                    onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}
                    disabled={paginationDisabled}
                  >
                    <SelectTrigger className="h-8 w-20 text-[13px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Resource detail drawer ────────────────────────────────────── */}
      <ResourceDrawer
        resourceGroup={drawerGroup}
        open={drawerGroup !== null}
        onClose={() => setDrawerGroup(null)}
        onNavigateToCalendar={() => {
          setDrawerGroup(null);
          openPlannerForReportRange();
        }}
        onResolveConflict={(resourceId) => {
          // Close the drawer first so the full-screen what-if overlay isn't
          // competing with it for the viewport.
          setDrawerGroup(null);
          handleResolveConflict(resourceId);
        }}
      />

      {/* ── Conflict resolution overlay ───────────────────────────────── */}
      <WhatIfConflictResolutionOverlay
        open={conflictOverlaySuggestion !== null}
        suggestions={conflictOverlaySuggestion ? [conflictOverlaySuggestion] : []}
        suggestion={conflictOverlaySuggestion}
        onSelectSuggestion={(s) => setConflictOverlaySuggestion(s)}
        risks={[]}
        resources={resources}
        bookings={bookings}
        globalWorkingProfile={settings.globalWorkingProfile}
        conflictItems={conflictOverlayItems}
        onClose={() => {
          setConflictOverlaySuggestion(null);
          setConflictOverlayItems([]);
        }}
        onPreviewCalendar={() => {
          setConflictOverlaySuggestion(null);
          setConflictOverlayItems([]);
          openPlannerForReportRange();
        }}
        onApply={(applied) => {
          // Persist the reassignment(s). Keep the overlay open so its
          // completion summary shows; closing happens via onClose.
          void handleCommitReassignments(applied);
        }}
      />
    </div>
  );
}
